import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/Functions';

const AdminFormSubmissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [formDetails, setFormDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal State for Viewing full data
  const [showModal, setShowModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchSubmissions = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_URL}/api/admin/forms/${id}/submissions`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      
      const res = await response.json();
      if (!response.ok) throw res;
      
      let parsedSubmissions = [];
      if (Array.isArray(res)) parsedSubmissions = res;
      else if (res.data && Array.isArray(res.data.data)) parsedSubmissions = res.data.data;
      else if (res.data && Array.isArray(res.data)) parsedSubmissions = res.data;
      else if (res.submissions && Array.isArray(res.submissions)) parsedSubmissions = res.submissions;
      
      setSubmissions(parsedSubmissions);
      if (res.form) setFormDetails(res.form);
    } catch (err) {
      setError(err.message || 'Failed to load submissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const confirmDelete = (sub) => {
    setItemToDelete(sub);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/submissions/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setSubmissions(submissions.filter(s => s.id !== itemToDelete.id));
      setItemToDelete(null);
      setShowModal(false);
      toast.success('Submission deleted successfully!');
    } catch (err) {
      toast.error('Error deleting submission.');
    }
  };

  const handleView = (sub) => {
    setSelectedSub(sub);
    setShowModal(true);
  };

  // Helper to extract a summary from the submission data
  const renderSummaryChips = (dataObj) => {
    if (!dataObj) return <span style={{color: '#94a3b8', fontSize: '14px', fontStyle: 'italic'}}>No data provided</span>;
    const keys = Object.keys(dataObj);
    if (keys.length === 0) return <span style={{color: '#94a3b8', fontSize: '14px', fontStyle: 'italic'}}>Empty submission</span>;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', lineHeight: '1.6' }}>
        {keys.slice(0, 3).map((k, index) => (
          <React.Fragment key={k}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k}:</span>
              <span style={{ color: '#0f172a', fontSize: '14.5px', fontWeight: 500 }}>
                {String(dataObj[k]).length > 25 ? String(dataObj[k]).substring(0, 25) + '...' : String(dataObj[k])}
              </span>
            </div>
            {index < Math.min(keys.length - 1, 2) && <span style={{ color: '#cbd5e1', margin: '0 4px' }}>•</span>}
          </React.Fragment>
        ))}
        {keys.length > 3 && (
          <>
            <span style={{ color: '#cbd5e1', margin: '0 4px' }}>•</span>
            <span style={{ background: '#f1f5f9', color: '#64748b', fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px' }}>+{keys.length - 3} more</span>
          </>
        )}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return {
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  return (
    <div style={{ background: '#f4f7f9', minHeight: '100vh', padding: '32px 40px', fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        .sub-header-card { background: linear-gradient(135deg, #061830 0%, #0a2540 100%); border-radius: 20px; padding: 32px 40px; margin-bottom: 32px; color: white; display: flex; align-items: center; gap: 24px; box-shadow: 0 12px 32px rgba(6, 24, 48, 0.15); }
        .sh-icon { width: 64px; height: 64px; border-radius: 18px; background: rgba(207, 160, 71, 0.15); border: 1px solid rgba(207, 160, 71, 0.3); display: flex; align-items: center; justify-content: center; font-size: 28px; color: #CFA047; flex-shrink: 0; }
        .sh-content h2 { margin: 0 0 6px; font-size: 28px; font-weight: 800; font-family: 'Outfit', sans-serif; letter-spacing: -0.5px; }
        .sh-content p { margin: 0; font-size: 15px; color: rgba(255, 255, 255, 0.7); }
        .sh-actions { margin-left: auto; }
        .sh-back-btn { background: rgba(255, 255, 255, 0.08); color: white; border: 1px solid rgba(255, 255, 255, 0.15); padding: 12px 24px; border-radius: 12px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 8px; font-family: 'Inter', sans-serif; }
        .sh-back-btn:hover { background: white; color: #061830; transform: translateY(-2px); }

        .premium-table-wrapper { background: white; border-radius: 16px; border: 1px solid #e8edf4; box-shadow: 0 8px 32px rgba(0,0,0,0.03); overflow: hidden; }
        .premium-table { width: 100%; border-collapse: separate; border-spacing: 0; text-align: left; }
        .premium-table th { background: #f8fafc; padding: 18px 24px; font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.8px; border-bottom: 1px solid #e8edf4; }
        .premium-table td { padding: 18px 24px; font-size: 14.5px; color: #1a2332; border-bottom: 1px solid #e8edf4; vertical-align: middle; transition: background 0.2s; }
        .premium-table tr:last-child td { border-bottom: none; }
        .premium-table tbody tr { cursor: pointer; }
        .premium-table tbody tr:hover td { background: #f8fafc; }
        
        .id-badge { background: #f1f5f9; color: #475569; padding: 4px 10px; border-radius: 6px; font-weight: 700; font-size: 13px; font-family: monospace; border: 1px solid #e2e8f0; }

        .sub-chips { display: flex; gap: 8px; flex-wrap: wrap; }
        .sub-chip { display: inline-flex; align-items: center; background: white; border: 1px solid #e2e8f0; border-radius: 6px; padding: 4px 10px; font-size: 13px; max-width: 100%; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
        .sc-label { color: #64748b; font-weight: 700; margin-right: 6px; text-transform: uppercase; font-size: 10.5px; letter-spacing: 0.5px; }
        .sc-val { color: #0f172a; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sub-chip-more { display: inline-flex; align-items: center; background: #f1f5f9; color: #475569; border-radius: 6px; padding: 4px 10px; font-size: 12px; font-weight: 700; border: 1px solid #e2e8f0; }

        .sr-actions { display: flex; gap: 8px; opacity: 0; transition: opacity 0.2s; justify-content: flex-end; }
        .premium-table tbody tr:hover .sr-actions { opacity: 1; }
        .btn-icon { width: 36px; height: 36px; border-radius: 8px; background: white; border: 1px solid #e2e8f0; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 14px; }
        .btn-icon:hover { background: #f8fafc; color: #0f172a; border-color: #cbd5e1; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
        .btn-icon.del:hover { background: #fef2f2; color: #ef4444; border-color: #fca5a5; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.1); }

        /* Modal Redesign */
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15,23,42,0.6); backdrop-filter: blur(8px); display: flex; justify-content: center; align-items: center; z-index: 999; padding: 20px; }
        .modal-content { background: white; border-radius: 24px; width: 100%; max-width: 800px; box-shadow: 0 24px 48px rgba(0,0,0,0.2); animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; }
        .modal-header { background: #f8fafc; padding: 32px 40px; border-bottom: 1px solid #e8edf4; display: flex; justify-content: space-between; align-items: flex-start; }
        .mh-info h3 { margin: 0 0 8px; font-size: 24px; font-weight: 800; font-family: 'Outfit', sans-serif; color: #0f172a; }
        .mh-meta { font-size: 14px; color: #64748b; display: flex; gap: 16px; align-items: center; }
        .mh-meta span { display: flex; align-items: center; gap: 6px; }
        .modal-close { background: white; border: 1px solid #e2e8f0; width: 40px; height: 40px; border-radius: 50%; font-size: 18px; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .modal-close:hover { background: #f1f5f9; color: #0f172a; transform: rotate(90deg); }
        .modal-body { padding: 40px; overflow-y: auto; background: white; }
        
        .data-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
        .data-item { background: #f8fafc; padding: 20px 24px; border-radius: 16px; border: 1px solid #e8edf4; transition: all 0.2s; }
        .data-item:hover { border-color: #cbd5e1; background: #f1f5f9; }
        .data-label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 700; color: #64748b; margin-bottom: 8px; }
        .data-val { font-size: 16px; color: #0f172a; font-weight: 500; word-break: break-word; line-height: 1.5; }

        @keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      {/* Premium Header */}
      <div className="sub-header-card">
        <div className="sh-icon"><i className="fa-solid fa-inbox"></i></div>
        <div className="sh-content">
          <h2>Submissions</h2>
          <p>{formDetails ? `Viewing submissions for "${formDetails.name}"` : 'Manage form submissions and inquiries.'}</p>
        </div>
        <div className="sh-actions">
          <button className="sh-back-btn" onClick={() => navigate('/admin/forms')}>
            <i className="fa-solid fa-arrow-left"></i> Back to Forms
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', color: '#ef4444', padding: '16px 20px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #fca5a5' }}>{error}</div>}

      {/* Premium Table */}
      <div className="premium-table-wrapper">
        <table className="premium-table">
          <thead>
            <tr>
              <th style={{ width: '80px' }}>ID</th>
              <th style={{ width: '180px' }}>Date Submitted</th>
              <th>Data Summary</th>
              <th style={{ width: '100px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '60px' }}>
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: '24px', marginBottom: '16px', color: '#CFA047' }}></i><br/>
                  <span style={{ color: '#64748b', fontSize: '14px' }}>Loading submissions...</span>
                </td>
              </tr>
            ) : submissions.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '80px 40px' }}>
                  <div style={{ width: '64px', height: '64px', background: '#f8fafc', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#cbd5e1', fontSize: '24px' }}>
                    <i className="fa-regular fa-folder-open"></i>
                  </div>
                  <h3 style={{ margin: '0 0 4px', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>No submissions yet</h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>This form hasn't received any submissions.</p>
                </td>
              </tr>
            ) : (
              submissions.map(sub => {
                const dt = formatDate(sub.created_at);
                return (
                  <tr key={sub.id} onClick={() => handleView(sub)}>
                    <td><span className="id-badge">#{sub.id}</span></td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#1a2332', marginBottom: '4px' }}>{dt.date}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>{dt.time}</div>
                    </td>
                    <td>{renderSummaryChips(sub.data)}</td>
                    <td>
                      <div className="sr-actions" onClick={e => e.stopPropagation()}>
                        <button className="btn-icon del" onClick={() => confirmDelete(sub)} title="Delete Submission">
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      {showModal && selectedSub && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="mh-info">
                <h3>Submission #{selectedSub.id}</h3>
                <div className="mh-meta">
                  <span><i className="fa-regular fa-calendar"></i> {new Date(selectedSub.created_at).toLocaleString()}</span>
                  <span><i className="fa-regular fa-user"></i> {selectedSub.user_id || 'Guest User'}</span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}><i className="fa-solid fa-xmark"></i></button>
            </div>
            <div className="modal-body">
              <div className="data-grid">
                {selectedSub.data && Object.keys(selectedSub.data).map(key => (
                  <div className="data-item" key={key}>
                    <div className="data-label">{key}</div>
                    <div className="data-val">{selectedSub.data[key] || <span style={{color: '#94a3b8', fontStyle: 'italic'}}>Empty</span>}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: '24px 40px', background: '#f8fafc', borderTop: '1px solid #e8edf4', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
              <button style={{ padding: '12px 24px', background: 'white', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={() => { setShowModal(false); confirmDelete(selectedSub); }}>
                <i className="fa-solid fa-trash"></i> Delete
              </button>
              <button style={{ padding: '12px 32px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => setShowModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="modal-overlay" onClick={() => setItemToDelete(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', padding: '40px', borderRadius: '24px', width: '400px', textAlign: 'center', boxShadow: '0 24px 48px rgba(0,0,0,0.2)', animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', margin: '0 auto 24px' }}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', margin: '0 0 12px', fontFamily: "'Outfit', sans-serif" }}>Delete Submission?</h3>
            <p style={{ fontSize: '15px', color: '#64748b', margin: '0 0 32px', lineHeight: 1.6 }}>
              Are you sure you want to delete submission <strong>#{itemToDelete.id}</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button style={{ flex: 1, padding: '14px', borderRadius: '12px', fontWeight: 600, fontSize: '15px', cursor: 'pointer', border: '1px solid #e2e8f0', background: 'white', color: '#475569', transition: 'all 0.2s' }} onClick={() => setItemToDelete(null)}>Cancel</button>
              <button style={{ flex: 1, padding: '14px', borderRadius: '12px', fontWeight: 600, fontSize: '15px', cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white', transition: 'all 0.2s', boxShadow: '0 8px 16px rgba(239, 68, 68, 0.2)' }} onClick={executeDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFormSubmissions;
